import React, { useMemo } from 'react';
import { StatusDot, StatusPill, StatusStripe, normalizeStatusTone } from './Status';
import Sparkline from './Sparkline';
import './KpiCard.css';

const TONE_LABELS = {
  good: 'Healthy',
  watch: 'Watch',
  behind: 'Behind',
  neutral: 'Awaiting data'
};

function formatDisplay(value) {
  if (value === null || value === undefined || value === '') {
    return 'Pending';
  }

  return value;
}

function toNumber(value) {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value !== 'string') {
    return NaN;
  }

  const normalized = value.replace(/[^0-9.-]/g, '');
  return normalized ? Number(normalized) : NaN;
}

function formatDelta(value, goal) {
  const actualValue = toNumber(value);
  const goalValue = toNumber(goal);

  if (!Number.isFinite(actualValue) || !Number.isFinite(goalValue)) {
    return goalValue ? `Goal ${formatDisplay(goal)}` : 'No target set';
  }

  const delta = actualValue - goalValue;
  if (delta === 0) {
    return 'At goal';
  }

  const formatted = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: Math.abs(delta) >= 10 ? 0 : 1
  }).format(Math.abs(delta));

  return delta > 0 ? `${formatted} over goal` : `${formatted} to goal`;
}

function KpiCard({ label, value, goal, metaLabel = '', status, history = [], variant = 'standard', onClick = null }) {
  const tone = normalizeStatusTone(status);
  const deltaLabel = useMemo(
    () => metaLabel || formatDelta(value, goal),
    [goal, metaLabel, value]
  );
  const interactive = typeof onClick === 'function';
  const interactiveProps = interactive
    ? {
      role: 'button',
      tabIndex: 0,
      onClick,
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(event);
        }
      },
      'aria-label': `${label || 'KPI'} — open details`
    }
    : {};

  // `highlight` and `modern` both render an inline status line (dot + label)
  // instead of a corner StatusPill. `modern` adds the executive-clean visual
  // language: flat surface, no drop shadow, tone rail only, tabular numerics.
  const useStatusLine = variant === 'highlight' || variant === 'modern';

  return (
    <article
      {...interactiveProps}
      className={`kpi-card kpi-card--${tone} kpi-card--${variant} ${interactive ? 'kpi-card--interactive' : ''}`.trim()}
    >
      <StatusStripe tone={tone} className="kpi-card__stripe" aria-hidden />
      <div className="kpi-card__header">
        <div className="kpi-card__title-stack">
          <p className="kpi-card__eyebrow">{label || 'KPI'}</p>
          {useStatusLine ? (
            <span className="kpi-card__status-line">
              <StatusDot tone={tone} size="sm" pulse={tone === 'behind'} />
              {TONE_LABELS[tone] || TONE_LABELS.neutral}
            </span>
          ) : null}
        </div>
        {useStatusLine
          ? null
          : <StatusPill tone={tone}>{TONE_LABELS[tone] || TONE_LABELS.neutral}</StatusPill>}
      </div>
      <div className="kpi-card__value">{formatDisplay(value)}</div>
      <div className="kpi-card__meta">
        <span className="kpi-card__goal">{deltaLabel}</span>
      </div>
      <Sparkline points={history} fallbackValue={toNumber(value)} tone={tone} className="kpi-card__sparkline" />
    </article>
  );
}

export default KpiCard;
